import { findIndexOfObjectInArray } from './array';
import { getId } from './item';

const toNumberOrNull = value => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const clampValue = (value, num) => {
  if (!Number.isFinite(value)) {
    return value;
  }
  let nextValue = value;
  const min = toNumberOrNull(num?.min);
  const max = toNumberOrNull(num?.max);
  if (min !== null && nextValue < min) {
    nextValue = min;
  }
  if (max !== null && nextValue > max) {
    nextValue = max;
  }
  return nextValue;
};

const roundValue = (value, rounded) => {
  if (!Number.isFinite(value) || !rounded) {
    return value;
  }
  if (rounded === 'up') {
    return Math.ceil(value);
  }
  if (rounded === 'nearest') {
    return Math.round(value);
  }
  return Math.floor(value);
};

const applyNumericOperation = (current, symbole, operand) => {
  if (!Number.isFinite(current)) {
    return current;
  }
  if (symbole === '=') {
    return Number.isFinite(operand) ? operand : current;
  }
  if (!Number.isFinite(operand)) {
    return current;
  }
  if (symbole === '+') {
    return current + operand;
  }
  if (symbole === '-') {
    return current - operand;
  }
  if (symbole === '*') {
    return current * operand;
  }
  if (symbole === '/' && operand !== 0) {
    return current / operand;
  }
  return current;
};

export const processNumData = (numData, item, items) => {
  const { num, numId, useMod, useCon } = numData;
  if (typeof num?.value !== 'number') {
    return { value: { error: 'erreur' }, modDelta: 0 };
  }
  const clampAndRound = nextValue =>
    roundValue(clampValue(nextValue, num), num?.rounded);
  const parsedMod = Number(num?.mod);
  const baseMod = Number.isFinite(parsedMod) ? parsedMod : 0;
  let rawBaseValue = num.value;
  if (num.formula) {
    rawBaseValue += processFormula(numData, item, items);
  }
  let value = rawBaseValue + (useMod ? baseMod : 0);
  let modDelta = 0;
  if (useCon) {
    const conditionResult = processCondition(
      value,
      rawBaseValue,
      baseMod,
      numId,
      item,
      items,
      useMod
    );
    value = conditionResult.value;
    modDelta = conditionResult.modDelta;
  }
  value = clampAndRound(value);
  return { value, modDelta };
};

export const processNum = (numData, item, items) => {
  return processNumData(numData, item, items).value;
};

export const regexformula = /(\s*[-+*/(),%?:=<>&!]+\s*)/;

const objectIdLikeRegex = /^[a-fA-F0-9]{24}$/;

const resolveNumIndexByIdOrLabel = (hasNum, ref) => {
  if (!Array.isArray(hasNum) || typeof ref !== 'string') {
    return -1;
  }
  const trimmed = ref.trim();
  if (!trimmed) {
    return -1;
  }
  let idx = findIndexOfObjectInArray(hasNum, trimmed);
  if (idx !== -1) {
    return idx;
  }
  // Fallback: allow referencing nums by label: [itemId.numLabel]
  return hasNum.findIndex(numObj => {
    const numId = getId(numObj);
    const num = numObj?.[numId];
    const label = typeof num?.label === 'string' ? num.label.trim() : '';
    return label !== '' && label === trimmed;
  });
};

const normalizeLabelRef = value => {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim().toLowerCase();
};

const resolveConditionIndexByIdOrLabel = (conditions, ref) => {
  if (!Array.isArray(conditions) || typeof ref !== 'string') {
    return -1;
  }
  const trimmed = ref.trim();
  if (!trimmed) {
    return -1;
  }

  const byIdIndex = findIndexOfObjectInArray(conditions, trimmed);
  if (byIdIndex !== -1) {
    return byIdIndex;
  }

  const normalizedRef = normalizeLabelRef(trimmed);
  if (!normalizedRef) {
    return -1;
  }

  const matchingIndexes = [];
  conditions.forEach((conditionObj, idx) => {
    const conditionId = getId(conditionObj);
    const condition = conditionObj?.[conditionId];
    const activeLabel = normalizeLabelRef(condition?.active?.label);
    const inactiveLabel = normalizeLabelRef(condition?.inactive?.label);
    if (
      (activeLabel && activeLabel === normalizedRef) ||
      (inactiveLabel && inactiveLabel === normalizedRef)
    ) {
      matchingIndexes.push(idx);
    }
  });

  // Ambiguous label: no deterministic condition.
  if (matchingIndexes.length !== 1) {
    return -1;
  }

  return matchingIndexes[0];
};

const sumNumsAcrossItems = (candidateIds, numRef, useMod, useCon, items) => {
  const ids = Array.from(new Set((candidateIds || []).filter(Boolean)));
  let sum = 0;
  let foundAny = false;
  for (const id of ids) {
    const linkedItem = items?.[id];
    if (!linkedItem?.has_num) {
      continue;
    }
    const numIdx = resolveNumIndexByIdOrLabel(linkedItem.has_num, numRef);
    if (numIdx === -1) {
      continue;
    }
    const numEntry = linkedItem.has_num[numIdx];
    const numId = getId(numEntry);
    const num = numEntry?.[numId];
    const resolved = processNum(
      { num, numId, useMod, useCon },
      linkedItem,
      items
    );
    if (typeof resolved === 'number' && Number.isFinite(resolved)) {
      sum += resolved;
      foundAny = true;
    }
  }
  return { sum, foundAny };
};

const resolveNoteItemIdsFromLabel = (label, item, items) => {
  const target = typeof label === 'string' ? label.trim() : '';
  if (!target) {
    return [];
  }

  const ids = new Set();

  // 1) Filter widgets notes (stored on the parent item)
  if (Array.isArray(item?.filters)) {
    item.filters.forEach(filterObj => {
      const filterId = getId(filterObj);
      const filter = filterObj?.[filterId];
      if (!filter || filter.noteNumEnabled !== true) {
        return;
      }
      const map = filter.noteNums && typeof filter.noteNums === 'object' ? filter.noteNums : null;
      if (!map) {
        return;
      }
      Object.keys(map).forEach(itemId => {
        if (!objectIdLikeRegex.test(itemId)) {
          return;
        }
        const linked = items?.[itemId];
        const linkedLabel = typeof linked?.label === 'string' ? linked.label.trim() : '';
        if (linkedLabel && linkedLabel === target) {
          ids.add(itemId);
        }
      });
    });
  }

  // 2) Reduced item widgets notes (stored on each tab's grid)
  if (Array.isArray(item?.tabs)) {
    item.tabs.forEach(tabObj => {
      const tabId = getId(tabObj);
      const tab = items?.[tabId];
      if (!Array.isArray(tab?.grid)) {
        return;
      }
      tab.grid.forEach(gridObj => {
        const widgetId = getId(gridObj);
        if (!widgetId || !objectIdLikeRegex.test(widgetId)) {
          return;
        }
        const cfg = gridObj?.[widgetId];
        if (!cfg || cfg.noteNumEnabled !== true) {
          return;
        }
        const widgetItem = items?.[widgetId];
        const widgetLabel = typeof widgetItem?.label === 'string' ? widgetItem.label.trim() : '';
        if (widgetLabel && widgetLabel === target) {
          ids.add(widgetId);
        }
      });
    });
  }

  return Array.from(ids);
};

const sumWidgetNotesForItemIds = (itemIds, item, items) => {
  const ids = Array.isArray(itemIds) ? itemIds.filter(Boolean) : [];
  if (ids.length === 0) {
    return { sum: 0, foundAny: false };
  }

  const targetSet = new Set(ids);
  let sum = 0;
  let foundAny = false;

  // Filter widgets notes
  if (Array.isArray(item?.filters)) {
    item.filters.forEach(filterObj => {
      const filterId = getId(filterObj);
      const filter = filterObj?.[filterId];
      if (!filter || filter.noteNumEnabled !== true) {
        return;
      }
      const map = filter.noteNums && typeof filter.noteNums === 'object' ? filter.noteNums : null;
      if (!map) {
        return;
      }
      Object.keys(map).forEach(id => {
        if (!targetSet.has(id)) {
          return;
        }
        const val = Number(map[id]);
        if (Number.isFinite(val)) {
          sum += val;
          foundAny = true;
        }
      });
    });
  }

  // Reduced item widgets notes (tab grids)
  if (Array.isArray(item?.tabs)) {
    item.tabs.forEach(tabObj => {
      const tabId = getId(tabObj);
      const tab = items?.[tabId];
      if (!Array.isArray(tab?.grid)) {
        return;
      }
      tab.grid.forEach(gridObj => {
        const widgetId = getId(gridObj);
        if (!widgetId || !targetSet.has(widgetId)) {
          return;
        }
        const cfg = gridObj?.[widgetId];
        if (!cfg || cfg.noteNumEnabled !== true) {
          return;
        }
        const val = Number(cfg.noteNum);
        if (Number.isFinite(val)) {
          sum += val;
          foundAny = true;
        }
      });
    });
  }

  return { sum, foundAny };
};

const computeFilterCandidateIds = (filter, currentItem, items) => {
  if (!filter || !currentItem || !items) {
    return [];
  }
  const required = Array.isArray(filter?.items) ? filter.items.filter(Boolean) : [];
  const excluded = Array.isArray(filter?.exclude) ? filter.exclude.filter(Boolean) : [];
  if (required.length === 0) {
    return [];
  }

  const requiredSet = new Set(required);
  const excludedSet = new Set(excluded);

  const candidates = [];
  Object.values(items).forEach(candidate => {
    if (!candidate?._id || candidate.__partial === true) {
      return;
    }
    // Basic scoping to avoid scanning unrelated universes when possible
    if (
      currentItem?.universe &&
      candidate?.universe &&
      candidate.universe !== currentItem.universe
    ) {
      return;
    }
    if (!Array.isArray(candidate?.links) || candidate.links.length === 0) {
      return;
    }
    const linkIds = candidate.links.map(obj => getId(obj)).filter(Boolean);
    if (linkIds.length === 0) {
      return;
    }
    const linkSet = new Set(linkIds);
    // Must have all required link ids
    for (const reqId of requiredSet) {
      if (!linkSet.has(reqId)) {
        return;
      }
    }
    // Must not have any excluded link ids
    for (const exId of excludedSet) {
      if (linkSet.has(exId)) {
        return;
      }
    }
    candidates.push(candidate._id);
  });

  return candidates;
};

function resolveFormulaExpression(expression, item, items) {
  if (typeof expression !== 'string' || expression.trim() === '') {
    return '';
  }
  const arrF = expression.split(regexformula);
  const replacedF = arrF.map(step => {
    let nextStep = step;
    nextStep = nextStep.replace('[', '').replace(']', '');
    const replacedArr = nextStep.split('.');
    const useMod = replacedArr.includes('useMod');
    const useCon = replacedArr.includes('useCon');
    const filteredParts = replacedArr.filter(
      part => part !== 'useMod' && part !== 'useCon'
    );

    if (filteredParts.length > 1) {
      const itemIdx = removeFirstLastSpace(filteredParts[0]);
      const entityId = removeFirstLastSpace(filteredParts[1]);
      const stateSpecifierRaw =
        filteredParts.length > 2 ? removeFirstLastSpace(filteredParts[2]) : null;

      // [filter.<filterId>.sum.<numRef>] => sum of a num across filter results
      // [filter.<filterId>.sumq.<numRef>] => sum of a num * quantity(note) across filter results
      if (itemIdx === 'filter' && filteredParts.length > 3) {
        const filterId = entityId;
        const op = removeFirstLastSpace(filteredParts[2]);
        const numRef = removeFirstLastSpace(filteredParts[3]);
        if (!filterId || !op || !numRef) {
          return step;
        }

        const filterEntry = Array.isArray(item?.filters)
          ? item.filters.find(filterObj => getId(filterObj) === filterId)
          : null;
        const filter = filterEntry ? filterEntry[filterId] : null;
        if (!filter) {
          return step;
        }

        const candidateIds = computeFilterCandidateIds(filter, item, items);
        if (candidateIds.length === 0) {
          // Empty filter results should behave like a 0-sum (inventory empty, etc.)
          return 0;
        }

        const { sum: rawSum, foundAny } = sumNumsAcrossItems(
          candidateIds,
          numRef,
          useMod,
          useCon,
          items
        );

        if (!foundAny) {
          // If the filter has items but none contains this num, keep the token to fail loudly.
          return step;
        }

        if (op === 'sum') {
          return rawSum;
        }

        if (op === 'sumq' || op === 'sumWeighted') {
          const noteEnabled = filter?.noteNumEnabled === true;
          const noteMap =
            filter?.noteNums && typeof filter.noteNums === 'object'
              ? filter.noteNums
              : {};

          let sum = 0;
          let hasWeight = false;
          candidateIds.forEach(candidateId => {
            const candidate = items?.[candidateId];
            if (!candidate?.has_num) {
              return;
            }
            const numIdx = resolveNumIndexByIdOrLabel(candidate.has_num, numRef);
            if (numIdx === -1) {
              return;
            }
            const numEntry = candidate.has_num[numIdx];
            const numId = getId(numEntry);
            const num = numEntry?.[numId];
            const value = processNum(
              { num, numId, useMod, useCon },
              candidate,
              items
            );
            if (typeof value !== 'number' || !Number.isFinite(value)) {
              return;
            }
            const qty = noteEnabled
              ? Number.isFinite(Number(noteMap?.[candidateId]))
                ? Number(noteMap[candidateId])
                : 1
              : 1;
            sum += value * qty;
            hasWeight = true;
          });
          return hasWeight ? sum : step;
        }

        return step;
      }

      // [itemId.links.constitution] => sum across current item's links that ALSO have a link to itemId.
      // This matches the "slot via links" model: items linked to both (current item) and (itemId).
      if (entityId === 'links' && filteredParts.length > 2) {
        const numRef = removeFirstLastSpace(filteredParts[2]);
        const linkIds = Array.isArray(item?.links)
          ? item.links.map(obj => getId(obj)).filter(Boolean)
          : [];
        const requiredLinkId =
          itemIdx === 'current'
            ? item?._id
            : items?.[itemIdx]?._id ||
              (objectIdLikeRegex.test(itemIdx) ? itemIdx : null);
        if (!requiredLinkId) {
          return step;
        }
        const candidateIds = linkIds.filter(linkId => {
          const linkedItem = items?.[linkId];
          if (!linkedItem?.links) {
            return false;
          }
          const linkedLinkIds = linkedItem.links.map(obj => getId(obj)).filter(Boolean);
          return linkedLinkIds.includes(requiredLinkId);
        });
        const { sum, foundAny } = sumNumsAcrossItems(
          candidateIds,
          numRef,
          useMod,
          useCon,
          items
        );
        if (foundAny) {
          return sum;
        }
        return step;
      }

      // "links" is a special selector to aggregate values across the current item's linked items.
      // Example: [links.constitution] => sum of all nums labeled "constitution" in current item's links.
      if (itemIdx === 'links' && Array.isArray(item?.links)) {
        const linkIds = item.links.map(obj => getId(obj)).filter(Boolean);
        const { sum, foundAny } = sumNumsAcrossItems(
          linkIds,
          entityId,
          useMod,
          useCon,
          items
        );
        if (foundAny) {
          return sum;
        }
        // Keep token as-is so it fails like a missing num reference.
        return step;
      }

      // "notes" is a special selector to aggregate widget notes from the current item.
      // Example: [notes.potion] => sum of all notes for widgets (reduced item widgets + filter widgets)
      // that reference the item named "potion" (or the item id if 24-hex string).
      if (itemIdx === 'notes') {
        const trimmed = typeof entityId === 'string' ? entityId.trim() : '';
        if (!trimmed) {
          return step;
        }
        const targetItemIds = objectIdLikeRegex.test(trimmed)
          ? [trimmed]
          : resolveNoteItemIdsFromLabel(trimmed, item, items);
        const { sum, foundAny } = sumWidgetNotesForItemIds(targetItemIds, item, items);
        if (foundAny) {
          return sum;
        }
        return step;
      }

      const itemForm = itemIdx === 'current' ? item : items?.[itemIdx];
      const isStateSpecifier =
        stateSpecifierRaw === 'active' || stateSpecifierRaw === 'inactive';

      if (isStateSpecifier && Array.isArray(itemForm?.conditions)) {
        const conditionIdx = resolveConditionIndexByIdOrLabel(
          itemForm.conditions,
          entityId
        );
        if (conditionIdx > -1) {
          const conditionId = getId(itemForm.conditions[conditionIdx]);
          const condition = itemForm.conditions[conditionIdx][conditionId];
          const isActive = condition?.state === 'active';
          nextStep =
            stateSpecifierRaw === 'active'
              ? isActive
              : condition?.state === 'inactive';
        } else {
          nextStep = false;
        }
        return nextStep;
      }

      if (itemForm?.has_num) {
        const numIdx = resolveNumIndexByIdOrLabel(itemForm.has_num, entityId);
        if (numIdx > -1) {
          const numEntry = itemForm.has_num[numIdx];
          const numId = getId(numEntry);
          const num = numEntry?.[numId];
          nextStep = processNum({ num, numId, useMod, useCon }, item, items);
        }
      }
    }
    return nextStep;
  });
  return replacedF.join('');
}

const urlParams = new URLSearchParams(window.location.search);
const processFormula = (numData, item, items) => {
  const { num } = numData;
  if (urlParams.get('mode') !== 'debug') {
    const formula = resolveFormulaExpression(num.formula, item, items);
    const result = calculate(formula);
    return roundValue(result, num?.rounded);
  }
};

function calculate(fn) {
  try {
    // eslint-disable-next-line no-new-func
    return new Function('return ' + fn)();
  } catch (e) {
    return 'formula error ';
  }
}

const removeFirstLastSpace = str => {
  if (str?.charAt(0) === ' ') {
    str = str.slice(1);
  }
  if (str?.charAt(str.length - 1) === ' ') {
    str = str.slice(0, -1);
  }
  return str;
};

export const evaluateMathExpression = (expression, item, items) => {
  if (typeof expression !== 'string' || expression.trim() === '') {
    return null;
  }
  const resolved = resolveFormulaExpression(expression, item, items);
  const result = calculate(resolved);
  return typeof result === 'number' && Number.isFinite(result)
    ? result
    : null;
};

const regexBooleanOperators = /\s*(&&|\|\|)\s*/;

const evaluateSingleComparison = (expression, item, items) => {
  if (
    typeof expression !== 'string' ||
    expression.trim() === ''
  ) {
    return true;
  }
  const regBefore = /^(.*?)[!<>=]/;
  const regAfter = /[!<>=]+(.*)/;
  const regSymbole = /[!<>=]+/;
  const beforeMatch = expression.match(regBefore);
  const afterMatch = expression.match(regAfter);
  const symboleMatch = expression.match(regSymbole);
  if (!beforeMatch || !afterMatch || !symboleMatch) {
    return true;
  }
  const compBefore = processFormula(
    {
      num: {
        formula: beforeMatch[1],
      },
    },
    item,
    items
  );
  const compAfter = processFormula(
    {
      num: {
        formula: afterMatch[1],
      },
    },
    item,
    items
  );
  const compSymbole = symboleMatch[0];

  if (
    compSymbole === '===' ||
    compSymbole === '==' ||
    compSymbole === '='
  ) {
    return compBefore === compAfter;
  }
  if (compSymbole === '!==' || compSymbole === '!=') {
    return compBefore !== compAfter;
  }
  if (compSymbole === '<' || compSymbole === '<=') {
    return compSymbole === '<=' ? compBefore <= compAfter : compBefore < compAfter;
  }
  if (compSymbole === '>' || compSymbole === '>=') {
    return compSymbole === '>=' ? compBefore >= compAfter : compBefore > compAfter;
  }
  return true;
};

export const evaluateConditionExpression = (conditionExpression, item, items) => {
  if (
    typeof conditionExpression !== 'string' ||
    conditionExpression.trim() === ''
  ) {
    return true;
  }
  const tokens = conditionExpression
    .split(regexBooleanOperators)
    .map(token => token.trim())
    .filter(token => token !== '');
  if (tokens.length === 0) {
    return true;
  }
  let result = evaluateSingleComparison(tokens[0], item, items);
  for (let i = 1; i < tokens.length; i += 2) {
    const operator = tokens[i];
    const nextExpression = tokens[i + 1];
    if (!nextExpression) {
      break;
    }
    const evaluation = evaluateSingleComparison(nextExpression, item, items);
    if (operator === '&&') {
      result = result && evaluation;
    } else if (operator === '||') {
      result = result || evaluation;
    }
  }
  return result;
};

const processCondition = (
  value,
  baseValue,
  baseMod,
  numId,
  item,
  items,
  includeMod
) => {
  let conditionValue = value;
  const numEntry = item?.has_num?.find(entry => getId(entry) === numId);
  const currentNum = numEntry?.[numId];
  const initialMod = Number.isFinite(baseMod)
    ? baseMod
    : Number(currentNum?.mod) || 0;
  let derivedMod = initialMod;
  let baseValueForMod;
  if (includeMod && Number.isFinite(conditionValue) && Number.isFinite(derivedMod)) {
    baseValueForMod = conditionValue - derivedMod;
  } else if (Number.isFinite(baseValue)) {
    baseValueForMod = baseValue;
  } else {
    baseValueForMod = Number.isFinite(value) ? value : 0;
  }
  if (item?.conditions) {
    for (const conditionObj of item?.conditions) {
      const condition = conditionObj[getId(conditionObj)];
      if (condition?.[condition?.state]?.fields) {
        for (const fieldId of Object.keys(condition[condition.state].fields)) {
          if (fieldId === numId) {
            let additionnalCondition = true;
            const conditionExpression =
              condition?.[condition?.state]?.condition;
            if (
              typeof conditionExpression === 'string' &&
              conditionExpression.trim() !== ''
            ) {
              additionnalCondition = evaluateConditionExpression(
                conditionExpression,
                item,
                items
              );
            }

            if (additionnalCondition) {
              const field = condition[condition.state].fields[fieldId];
              const apply = field.value?.split
                ? processFormula({ num: { formula: field.value } }, item, items)
                : field.value;
              const numericApply = Number(apply);
              const target = field?.target || 'value';
              const shouldApplyToMod =
                target === 'mod' && field.type === 'has_num';
              if (shouldApplyToMod) {
                const nextMod = applyNumericOperation(
                  derivedMod,
                  field.symbole,
                  numericApply
                );
                if (Number.isFinite(nextMod)) {
                  derivedMod = nextMod;
                  if (includeMod && Number.isFinite(baseValueForMod)) {
                    conditionValue = baseValueForMod + derivedMod;
                  }
                }
              } else {
                if (field.symbole === '=') {
                  conditionValue = Number.isFinite(numericApply)
                    ? numericApply
                    : apply;
                } else if (
                  Number.isFinite(conditionValue) &&
                  Number.isFinite(numericApply)
                ) {
                  conditionValue = applyNumericOperation(
                    Number(conditionValue),
                    field.symbole,
                    numericApply
                  );
                }
                if (includeMod && Number.isFinite(conditionValue) && Number.isFinite(derivedMod)) {
                  baseValueForMod = conditionValue - derivedMod;
                } else if (!includeMod && Number.isFinite(conditionValue)) {
                  baseValueForMod = conditionValue;
                }
              }
            }
          }
        }
      }
    }
  }
  const modDelta = Number.isFinite(derivedMod - initialMod)
    ? derivedMod - initialMod
    : 0;
  return { value: conditionValue, modDelta };
};
